// Gestão de Pacotes Disponíveis para SaaS
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Badge } from '../../../components/common/Badge';
import { Plus, Edit, Trash2, Package, Star, AlertCircle, CheckCircle, X } from 'lucide-react';
import { formatCurrency } from '../../../utils/formatters';
import { getPacotes, createPacote, updatePacote, deletePacote } from '../../../services/pacotesService';
import type { Pacote } from '../../../services/pacotesService';

interface FormularioPacoteProps {
  pacote: Pacote | null;
  onSave: (pacote: Omit<Pacote, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  error: string | null;
}

function FormularioPacote({ pacote, onSave, onCancel, isSaving, error }: FormularioPacoteProps) {
  const [formData, setFormData] = useState({
    nome: pacote?.nome || '',
    descricao: pacote?.descricao || '',
    valorMensal: pacote?.valorMensal || 0,
    valorAnual: pacote?.valorAnual || 0,
    usuarios: pacote?.usuarios || 1,
    storageDados: pacote?.storageDados || '',
    storageArquivos: pacote?.storageArquivos || '',
    features: pacote?.features || [] as string[],
    ativo: pacote?.ativo ?? true,
    destacado: pacote?.destacado ?? false,
  });

  const [novaFeature, setNovaFeature] = useState('');

  const adicionarFeature = () => {
    if (novaFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, novaFeature.trim()],
      });
      setNovaFeature('');
    }
  };

  const removerFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome do Pacote"
          value={formData.nome}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          required
        />
        <Input
          label="Descrição"
          value={formData.descricao}
          onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
        />
        <Input
          label="Valor Mensal (R$)"
          type="number"
          step="0.01"
          min="0"
          value={formData.valorMensal}
          onChange={(e) => setFormData({ ...formData, valorMensal: Number(e.target.value) })}
          required
        />
        <Input
          label="Valor Anual (R$)"
          type="number"
          step="0.01"
          min="0"
          value={formData.valorAnual}
          onChange={(e) => setFormData({ ...formData, valorAnual: Number(e.target.value) })}
          required
        />
        <Input
          label="Número de Usuários"
          type="number"
          min="1"
          value={formData.usuarios}
          onChange={(e) => setFormData({ ...formData, usuarios: Number(e.target.value) })}
          required
        />
        <Input
          label="Storage de Dados"
          value={formData.storageDados}
          onChange={(e) => setFormData({ ...formData, storageDados: e.target.value })}
          placeholder="Ex: 60 MB, 120 MB"
        />
        <Input
          label="Storage de Arquivos"
          value={formData.storageArquivos}
          onChange={(e) => setFormData({ ...formData, storageArquivos: e.target.value })}
          placeholder="Ex: 1.5 GB, 3 GB"
        />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Especificações (Features)
        </label>
        <div className="space-y-2">
          {formData.features.map((feature, index) => (
            <div key={index} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <span className="flex-1 text-gray-900 dark:text-gray-100">{feature}</span>
              <button
                type="button"
                onClick={() => removerFeature(index)}
                className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Remover especificação"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              placeholder="Adicionar nova especificação"
              value={novaFeature}
              onChange={(e) => setNovaFeature(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarFeature();
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={adicionarFeature}
              disabled={!novaFeature.trim()}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="ativo"
            checked={formData.ativo}
            onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <label htmlFor="ativo" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Pacote Ativo
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="destacado"
            checked={formData.destacado}
            onChange={(e) => setFormData({ ...formData, destacado: e.target.checked })}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <label htmlFor="destacado" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Destacar Pacote
          </label>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSaving}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={isSaving}>
          {isSaving ? 'Salvando...' : pacote ? 'Atualizar Pacote' : 'Criar Pacote'}
        </Button>
      </div>
    </form>
  );
}

export function Pacotes() {
  const [busca, setBusca] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pacoteSelecionado, setPacoteSelecionado] = useState<Pacote | null>(null);
  const [pacotes, setPacotes] = useState<Pacote[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    carregarPacotes();
  }, []);

  const carregarPacotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const pacotesData = await getPacotes();
      setPacotes(pacotesData);
    } catch (err: any) {
      console.error('[Pacotes] Erro ao carregar pacotes:', err);
      setError(err.message || 'Erro ao carregar pacotes');
    } finally {
      setLoading(false);
    }
  };

  const pacotesFiltrados = pacotes.filter((p) =>
    p.nome.toLowerCase().includes(busca.toLowerCase()) ||
    p.descricao.toLowerCase().includes(busca.toLowerCase())
  );

  const abrirModal = (pacote?: Pacote) => {
    setPacoteSelecionado(pacote || null);
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setPacoteSelecionado(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Pacotes' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestão de Pacotes</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerenciar pacotes disponíveis para assinaturas</p>
        </div>
        <Button variant="primary" onClick={() => abrirModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Pacote
        </Button>
      </div>

      {/* Busca */}
      <Card>
        <Input
          label="Buscar Pacote"
          placeholder="Nome ou descrição"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Card>

      {/* Tabela */}
      <Card>
        <Table
          data={pacotesFiltrados}
          columns={[
            {
              key: 'nome',
              label: 'Nome',
              render: (item: Pacote) => (
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-gray-400" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.nome}</span>
                      {item.destacado && (
                        <Badge variant="success">
                          <Star className="w-3 h-3 mr-1" />
                          Destaque
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.descricao}</p>
                  </div>
                </div>
              ),
            },
            {
              key: 'valorMensal',
              label: 'Valor Mensal',
              render: (item: Pacote) => formatCurrency(item.valorMensal),
            },
            {
              key: 'valorAnual',
              label: 'Valor Anual',
              render: (item: Pacote) => formatCurrency(item.valorAnual),
            },
            {
              key: 'usuarios',
              label: 'Usuários',
              render: (item: Pacote) => `${item.usuarios} usuários`,
            },
            {
              key: 'storageDados',
              label: 'Armazenamento',
              render: (item: Pacote) => `${item.storageDados} / ${item.storageArquivos}`,
            },
            {
              key: 'ativo',
              label: 'Status',
              render: (item: Pacote) => (
                <Badge variant={item.ativo ? 'success' : 'danger'}>
                  <div className="flex items-center gap-1">
                    {item.ativo ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : (
                      <AlertCircle className="w-3 h-3" />
                    )}
                    <span>{item.ativo ? 'Ativo' : 'Inativo'}</span>
                  </div>
                </Badge>
              ),
            },
            {
              key: 'actions',
              label: 'Ações',
              render: (item: Pacote) => (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => abrirModal(item)}
                    className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                    title="Editar pacote"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={async () => {
                      if (confirm('Tem certeza que deseja excluir este pacote?')) {
                        try {
                          await deletePacote(item.id);
                          await carregarPacotes();
                        } catch (err: any) {
                          console.error('[Pacotes] Erro ao excluir pacote:', err);
                          setError(err.message || 'Erro ao excluir pacote');
                        }
                      }
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Excluir pacote"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Modal de Edição/Criação */}
      <Modal
        isOpen={isModalOpen}
        onClose={fecharModal}
        title={pacoteSelecionado ? 'Editar Pacote' : 'Novo Pacote'}
        size="lg"
      >
        <FormularioPacote
          pacote={pacoteSelecionado}
          onSave={async (pacoteData) => {
            try {
              setIsSaving(true);
              setError(null);
              
              if (pacoteSelecionado) {
                await updatePacote(pacoteSelecionado.id, pacoteData);
              } else {
                await createPacote(pacoteData);
              }
              
              await carregarPacotes();
              fecharModal();
            } catch (err: any) {
              console.error('[Pacotes] Erro ao salvar pacote:', err);
              setError(err.message || 'Erro ao salvar pacote');
              throw err;
            } finally {
              setIsSaving(false);
            }
          }}
          onCancel={fecharModal}
          isSaving={isSaving}
          error={error}
        />
      </Modal>
    </div>
  );
}

